import React, { useState, useEffect, useRef } from "react";
import GenericTable from "../../../Components/GenericTable";
import { Button, Input, Dropdown, message } from "antd";
import {
  SearchOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import "./style.css";
import { useNavigate } from "react-router-dom";
import { clientAPI } from "../../../utils/api";

const ClientList = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchTerm, setSearchTerm] = useState("");
  // Tracks the most recently-fired request so a slow earlier response can't
  // overwrite the table with stale results after a faster later one lands.
  const latestRequestRef = useRef(0);

  // Fetch clients from API — search and ordering are both handled server-side
  const fetchClients = async (search = "", ordering = "name") => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);
      const response = await clientAPI.getClients(search, ordering);
      if (requestId !== latestRequestRef.current) return; // a newer request has since started

      const formattedClients = response.results.data.map((client) => ({
        key: client.id,
        clientName: client.name,
        email: client.email,
        phone: client.phone,
        address: client.address,
        id: client.id,
      }));

      setClients(formattedClients);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      messageApi.error(error.message || "Failed to fetch clients");
      console.error("Fetch clients error:", error);
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  };

  // Load alphabetically on mount, then debounce server-side search as the user types
  useEffect(() => {
    const delay = searchTerm === "" ? 0 : 300;
    const timer = setTimeout(() => {
      fetchClients(searchTerm);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleEdit = (record) => {
    navigate(`/add-client`, { state: { clientId: record.id } });
  };

  const handleDelete = async (record) => {
    try {
      await clientAPI.deleteClient(record.id);
      setClients((prev) => prev.filter((c) => c.key !== record.key));
      messageApi.success(`${record.clientName} deleted successfully`);
    } catch (error) {
      messageApi.error(error.message || "Failed to delete client");
    }
  };

  const columns = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      sorter: (a, b) => a.clientName.localeCompare(b.clientName),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MailOutlined style={{ color: "#3A974C" }} />
          {text}
        </span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <PhoneOutlined style={{ color: "#3980F2" }} />
          {text}
        </span>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <EnvironmentOutlined style={{ color: "#E2AE03" }} />
          {text}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          {
            key: "edit",
            icon: <EditOutlined style={{ color: "#3980F2" }} />,
            label: "Edit",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              handleEdit(record);
            },
          },
          {
            key: "delete",
            icon: <DeleteOutlined style={{ color: "#D11A2A" }} />,
            label: "Delete",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              handleDelete(record);
            },
          },
        ];

        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <span
              onClick={(e) => e.stopPropagation()}
              style={{
                fontSize: 20,
                cursor: "pointer",
                display: "inline-block",
                transform: "rotate(90deg)",
              }}
            >
              <MoreOutlined />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  const handleAddNewBtn = () => {
    navigate("/add-client");
  };

  return (
    <div className="client-list">
      {contextHolder}
      <div
        className="btn"
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search by name, email, phone, or address"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Button onClick={handleAddNewBtn} icon={<PlusOutlined />}>
          Add New
        </Button>
      </div>
      <GenericTable
        title="Client Details"
        columns={columns}
        dataSource={clients}
        loading={loading}
        onRowClick={(record) => {
          navigate("/view-client-detail", { state: { clientId: record.id } });
        }}
      />
    </div>
  );
};

export default ClientList;